import { agentsVerificationsApi } from '../../../data/agentsVerificationsApi';

/**
 * Feature-owned minimal projection of the Verifications `GET /verification?id=` response — only
 * the fields the runs feature consumes. The backend returns the full composite `VerificationView`;
 * typing just this surface keeps the feature decoupled from the Verifications contract.
 */
interface VerificationCaseDto {
  id?: string;
  accountNumber?: string;
  patientFirstName?: string;
  patientLastName?: string;
  patientBirthDate?: string;
  serviceDate?: string;
  primaryPayerName?: string;
  physicianFirstName?: string;
  physicianLastName?: string;
}

/**
 * The case context behind a run's `verification` subject, expressed in the runs feature's own
 * vocabulary. Components depend on this view + hook, never on the Verifications contract.
 */
export interface RunSubjectCaseView {
  patientName: string;
  patientBirthDate?: string;
  serviceDate?: string;
  physicianName: string;
  payerName: string;
  accountNumber: string;
}

const joinName = (first?: string, last?: string): string => `${first ?? ''} ${last ?? ''}`.trim() || '—';

const toRunSubjectCaseView = (dto: VerificationCaseDto): RunSubjectCaseView => {
  return {
    patientName: joinName(dto.patientFirstName, dto.patientLastName),
    patientBirthDate: dto.patientBirthDate,
    serviceDate: dto.serviceDate,
    physicianName: joinName(dto.physicianFirstName, dto.physicianLastName),
    payerName: dto.primaryPayerName || '—',
    accountNumber: dto.accountNumber || '—',
  };
};

/**
 * Reads a single verification by its composite id (`{company}-{center}-{visitKey}`) via the
 * existing `GET /verification?id=` endpoint, projected straight into the feature's view. This read
 * is worklist-independent — it resolves the aggregate directly, so the patient-run dialog keeps
 * working after a case leaves the worklist.
 */
const runSubjectCaseEndpoints = agentsVerificationsApi.injectEndpoints({
  endpoints: (builder) => ({
    getRunSubjectCase: builder.query<RunSubjectCaseView, { id: string }>({
      query: ({ id }) => ({
        url: '/verification',
        method: 'GET',
        params: { id },
      }),
      transformResponse: (response: VerificationCaseDto) => toRunSubjectCaseView(response),
      providesTags: (_result, _error, { id }) => [{ type: 'Verification', id }],
    }),
  }),
  overrideExisting: false,
});

export const { useGetRunSubjectCaseQuery } = runSubjectCaseEndpoints;
